import { useState, useEffect, useRef } from 'react';
import { theme } from '../../theme';

// ── Role-grouped FAQ data ─────────────────────────────────────────────────────

const FAQ_BY_ROLE = {

  employee: [
    {
      category: '🏠 My Dashboard',
      items: [
        { q: 'What can I see on My Dashboard?', a: 'Your dashboard shows today\'s attendance status, pending leave balance, recent notifications, upcoming leave, WFH schedule, and quick links to frequently used features.' },
        { q: 'How do I navigate between modules?', a: 'Use the left sidebar to navigate. Click any section heading to expand it. If collapsed, click the › arrow on the left edge to expand.' },
      ],
    },
    {
      category: '📅 Leave Management',
      items: [
        { q: 'How do I apply for leave?', a: 'Go to Leave & Time → My Leave. Click "Apply Leave", choose the leave type (Casual, Sick, Earned, etc.), select dates, enter a reason, and click Submit. Your reporting manager will receive a notification to approve.' },
        { q: 'Can I cancel a submitted leave request?', a: 'Yes. Go to My Leave, find the pending request, and click Cancel. Once approved, contact HR to reverse it.' },
        { q: 'Where do I see my leave balance?', a: 'On the My Leave page, the top cards show your available balance for each leave type (Casual, Sick, Earned, etc.).' },
        { q: 'What happens after I apply for leave?', a: 'Your reporting manager receives an email notification. Once approved or rejected, you\'ll receive a notification in the bell icon and an email update.' },
      ],
    },
    {
      category: '🏠 Work From Home (WFH)',
      items: [
        { q: 'How do I request WFH?', a: 'Go to Leave & Time → My WFH. Click "Request WFH", pick the date and reason, then submit. Your manager will approve or reject it.' },
        { q: 'Is there a WFH limit per month?', a: 'Yes, HR sets WFH limits per department. The system will alert you if you exceed the monthly limit. Check your remaining WFH balance on the My WFH page.' },
      ],
    },
    {
      category: '⏰ Attendance',
      items: [
        { q: 'How do I view my attendance?', a: 'Go to Leave & Time → My Attendance. You can see daily check-in/check-out records, working hours, and attendance status for each day.' },
        { q: 'What if my attendance is missing or incorrect?', a: 'Go to My Attendance and submit a manual check-in request with the correct time and a reason. Your reporting manager must approve it first, then HR gives final approval.' },
        { q: 'What do the attendance statuses mean?', a: 'Present = checked in normally. WFH = working from home. On Leave = approved leave. Absent = no check-in recorded. Manual = manually entered record pending approval.' },
      ],
    },
    {
      category: '📍 Client Visit',
      items: [
        { q: 'How do I log a client visit?', a: 'Go to Leave & Time → Client Visit. Click "New Request", enter the visit date, client name, location, and purpose. Submit for manager approval.' },
      ],
    },
    {
      category: '⏱️ Timesheet',
      items: [
        { q: 'How do I submit my weekly timesheet?', a: 'Go to Leave & Time → My Timesheet. Log hours against each project for the week. Click Submit before Friday end-of-day. You\'ll receive a reminder email if not submitted.' },
        { q: 'Can I edit a submitted timesheet?', a: 'Timesheets can be edited before the submission deadline. After that, contact your project manager or HR.' },
      ],
    },
    {
      category: '💰 Expense Claims',
      items: [
        { q: 'How do I submit an expense claim?', a: 'Go to Finance → My Claims. Click "New Claim", fill in expense details (category, amount, date, description), attach receipts, and submit. Finance team will review and approve.' },
        { q: 'What expense categories are supported?', a: 'Travel, Food, Accommodation, Communication, Office Supplies, and Other. Select the most appropriate category when submitting.' },
        { q: 'How do I track my claim status?', a: 'On My Claims page, each claim shows: Draft, Submitted, Approved, Rejected, or Paid. Click any claim to see the full history.' },
      ],
    },
    {
      category: '📄 Payslips',
      items: [
        { q: 'Where can I view my payslips?', a: 'Go to Finance → My Payslips. All monthly payslips are listed. Click any month to view and download the payslip as a PDF.' },
      ],
    },
    {
      category: '🖥️ IT Assets & Tickets',
      items: [
        { q: 'How do I raise an IT support ticket?', a: 'Go to Assets & IT → My Tickets. Click "New Ticket", describe your issue, set priority (Low/Medium/High/Critical), and submit. The IT team will be notified and assigned.' },
        { q: 'Where can I see assets assigned to me?', a: 'Go to Assets & IT → My Assets. All laptops, accessories, and software licenses assigned to you are listed here.' },
        { q: 'How do I track my ticket progress?', a: 'Go to My Tickets. Each ticket shows its status: Open → In Progress → Resolved. You\'ll receive a notification when IT resolves your ticket — you can Acknowledge or Reopen it.' },
      ],
    },
    {
      category: '👤 My Profile',
      items: [
        { q: 'How do I update my personal information?', a: 'Click your name in the top-right → My Profile. You can update personal details, emergency contacts, bank details, qualifications, and upload your profile photo.' },
        { q: 'How do I change my password?', a: 'Click your name in the top-right → Change Password. Enter your current password and set a new one (8–12 characters, uppercase, lowercase, number, special character).' },
        { q: 'What is the Profile Verification process?', a: 'After completing your profile, submit it for HR verification. Status moves: Not Submitted → Pending Review → Approved/Rejected. HR may ask for corrections before approving.' },
      ],
    },
    {
      category: '🎯 Performance & Training',
      items: [
        { q: 'How do I view my performance review?', a: 'Go to Growth & Career → My Performance. You can see self-assessment forms, ratings, and feedback from your manager.' },
        { q: 'Where can I find assigned training modules?', a: 'Go to Growth & Career → My Training. Training programs assigned to you by HR are listed here with their status and deadlines.' },
      ],
    },
  ],

  hr_admin: [
    {
      category: '👥 Employee Management',
      items: [
        { q: 'How do I add a new employee?', a: 'Go to People → Employees → "+ Add Employee". Fill in personal details, department, position, reporting manager, and employment type. The employee receives a welcome email with login credentials.' },
        { q: 'How do I edit an existing employee record?', a: 'Go to People → Employees, find the employee, and click Edit. You can update all fields including department, position, pay details, and reporting manager.' },
        { q: 'How do I deactivate an employee who has left?', a: 'Go to Employees, find the employee, and click Deactivate. This sets their status to Inactive and revokes system access.' },
        { q: 'How do I verify an employee\'s submitted profile?', a: 'Go to People → Profile Verification. Employees who have submitted their profile appear here. Review and click Approve or Reject. Rejected profiles return to the employee for correction.' },
        { q: 'How do I filter employees by department or team?', a: 'On the Employees page, use the Department dropdown and Team type-search dropdown to filter. You can also filter by Status (Active/Inactive) and Profile Status.' },
      ],
    },
    {
      category: '📅 Leave Management',
      items: [
        { q: 'How do I approve or reject a leave request?', a: 'Go to Time & Leave → Leave Management. Pending requests are listed. Click Approve or Reject. Rejected requests require a reason which is sent to the employee.' },
        { q: 'Can HR override a leave decision?', a: 'Yes. HR Admins can approve or reject any leave request, including those already handled by managers.' },
        { q: 'How do I view leave balance for an employee?', a: 'Open the employee profile from People → Employees and check the Leave Balance section. You can also filter by employee on the Leave Management page.' },
      ],
    },
    {
      category: '⏰ Attendance',
      items: [
        { q: 'How does the two-stage attendance approval work?', a: 'When an employee submits a manual check-in: (1) Their reporting manager approves first — status becomes "Pending HR". (2) HR gives final approval. Both stages must complete before it is marked Approved.' },
        { q: 'How do I approve manual attendance records?', a: 'Go to Time & Leave → Attendance Approval. Records pending HR approval are shown. Approve or Reject each. Records still awaiting manager approval are visible but not actionable yet.' },
        { q: 'How do I access biometric attendance data?', a: 'Go to Time & Leave → Biometric Attendance. You can view AMS records, diagnose sync issues, and relink orphaned biometric records.' },
        { q: 'How do I view WFH limit setup?', a: 'Go to Time & Leave → WFH Limit Setup. Set the monthly WFH quota per department or for individual employees.' },
      ],
    },
    {
      category: '💰 Payroll & Compensation',
      items: [
        { q: 'How do I run payroll for a month?', a: 'Go to Compensation → Payroll. Select the month, verify employee salary details, and process payroll. Payslips are generated automatically and become available to employees.' },
        { q: 'How do I set up pay fixation for a new employee?', a: 'Go to Compensation → Pay Fixation. Select the employee and enter their CTC breakdown (basic, HRA, allowances, deductions). Save to activate for the next payroll cycle.' },
      ],
    },
    {
      category: '🎯 Talent & Recruitment',
      items: [
        { q: 'How do I manage interview candidates?', a: 'Go to Talent → Interviews. Add candidates, track interview stages, and convert selected candidates to employees directly from this page.' },
        { q: 'How do I process a promotion?', a: 'Go to Talent → Promotions. Select the employee, enter new designation, department, and effective date. The change is reflected in the employee profile.' },
        { q: 'How do I transfer an employee to another department?', a: 'Go to Talent → Transfers. Select the employee, target department, reporting manager, and effective date. Confirm to update the employee record.' },
      ],
    },
    {
      category: '📢 Engagement',
      items: [
        { q: 'How do I post an announcement?', a: 'Go to Engagement → Announcements → New Announcement. Set the title, content, target audience (all / specific department), and publish. All targeted employees will see it on their dashboard.' },
        { q: 'How do I add a Hall of Fame recognition?', a: 'Go to Engagement → Hall of Fame → Add Recognition. Choose the employee, award type, and month. It appears on the employee\'s dashboard and company-wide feed.' },
      ],
    },
    {
      category: '🏢 Departments & Org',
      items: [
        { q: 'How do I create a new department?', a: 'Go to People → Departments → Add Department. Enter the name and optional head. Teams can be added under each department.' },
        { q: 'How do I view the organisation chart?', a: 'Go to People → Org Chart. The chart shows the full reporting hierarchy. You can zoom, search employees, and view their reporting chain.' },
      ],
    },
  ],

  it_admin: [
    {
      category: '💻 Asset Management',
      items: [
        { q: 'How do I add a new asset?', a: 'Go to Asset Management → Assets → Add Asset. Enter asset name, category, serial number, purchase date, warranty expiry, and condition. Save to register it in the system.' },
        { q: 'How do I assign an asset to an employee?', a: 'Open the asset, click Assign, then search and select the employee. The asset status changes to "Assigned" and appears in the employee\'s My Assets.' },
        { q: 'How do I mark an asset as returned?', a: 'Open the asset and click Return. The status reverts to "Available" and the assignment is closed with a return date.' },
        { q: 'How do I manage software licenses?', a: 'Go to Asset Management → Licenses. Add license details (product, vendor, seats, expiry). Assign seats to employees. The system alerts when licenses expire within 90 days.' },
        { q: 'How do I bulk import assets?', a: 'Go to Assets and click Bulk Import. Download the CSV template, fill in asset details, and upload. The system validates and imports all records.' },
      ],
    },
    {
      category: '🎫 Ticket Management',
      items: [
        { q: 'How do I assign a ticket to IT staff?', a: 'Open the ticket. In the Update Ticket panel on the right, use the "Assign To" type-search dropdown — only IT Admin and IT Support staff appear. Select and save.' },
        { q: 'How do I resolve a ticket?', a: 'Open the ticket, change Status to "Resolved", add Resolution Notes, and save. The employee receives an email and can Acknowledge or Reopen the ticket.' },
        { q: 'What are ticket priority levels?', a: 'Low = minor inconvenience. Medium = workflow impacted. High = significant impact. Critical = system down or data loss. Set priority accurately so the team can prioritize.' },
      ],
    },
    {
      category: '📊 IT Dashboard',
      items: [
        { q: 'What does the IT Dashboard show?', a: 'Total assets, asset health distribution, open tickets by priority, assets by category, expiring warranties (next 90 days), and license expiry alerts.' },
        { q: 'How do I view expiring warranties?', a: 'The IT Dashboard → Expiring Warranties section shows assets with warranty expiring in 90 days. Red = expires within 30 days. Click any row to open the asset.' },
      ],
    },
  ],

  it_support: [
    {
      category: '🎫 My Queue & Tickets',
      items: [
        { q: 'How do I see tickets assigned to me?', a: 'Go to Support → My Queue. All tickets assigned to you are listed by priority. You can update status, add comments, and resolve from here.' },
        { q: 'How do I update a ticket status?', a: 'Open the ticket. In the Update Ticket panel, change Status to In Progress, Resolved, or Closed. Add Resolution Notes when resolving. Save Changes.' },
        { q: 'How do I add a comment to a ticket?', a: 'Open the ticket, scroll to Comments, type your update and click Add Comment. Both the employee and IT team can see all comments.' },
      ],
    },
  ],

  finance_executive: [
    {
      category: '💰 Claims Processing',
      items: [
        { q: 'How do I review and approve expense claims?', a: 'Go to Finance → Claims. Claims with "Submitted" status await your review. Open a claim to see all items, receipts, and amounts. Click Approve or Reject. Rejected claims return to the employee.' },
        { q: 'How do I mark a claim as paid?', a: 'After approving a claim, mark it as Paid once the reimbursement is processed. This updates the claim status and notifies the employee.' },
        { q: 'How do I generate a monthly expense report?', a: 'Go to Finance → Monthly Reports. Select the employee and month. The report shows all approved claims with amounts. Download as PDF or Excel.' },
      ],
    },
    {
      category: '📊 Finance Dashboard',
      items: [
        { q: 'What does the Finance Dashboard show?', a: 'Total claims submitted, amount Waiting for Verification (pending approval), total approved amount, and Pending amount (approved but not yet paid).' },
      ],
    },
  ],

  ceo: [
    {
      category: '📊 Executive Dashboard',
      items: [
        { q: 'What does the Executive Dashboard show?', a: 'Headcount overview, department-wise distribution, recent hires, attendance trends (last 6 months), leave utilisation, project status summary, and financial highlights.' },
        { q: 'How do I view new joiners this month?', a: 'Click the "New Joiners" card on the Executive Dashboard, or go to Employees and use the "New Joiners This Month" filter.' },
        { q: 'How do I see employees on probation?', a: 'Click the "Probation Due" card on the dashboard. This shows employees whose 90-day probation is ending within 30 days.' },
      ],
    },
    {
      category: '📁 Projects',
      items: [
        { q: 'How do I view project status and financials?', a: 'Go to Projects, click any project to see its dashboard — timeline, issues, team members, and financials (budget vs actuals). CFO and CEO have access to the financials tab.' },
      ],
    },
  ],

  reporting_manager: [
    {
      category: '👥 Team Management',
      items: [
        { q: 'How do I approve team leave requests?', a: 'Go to Leave & Time → My Leave → Team Requests tab (or use the notification bell). Pending requests from your reportees appear here. Click Approve or Reject.' },
        { q: 'How do I approve team WFH requests?', a: 'Go to Leave & Time → My WFH → Team Requests tab. Review and approve/reject WFH requests from your direct reports.' },
        { q: 'How do I approve manual attendance requests (Stage 1)?', a: 'Go to Leave & Time → Team Attendance Approval. Manual check-in requests from your reportees appear here for Stage 1 approval. After your approval, it goes to HR for final approval.' },
        { q: 'How do I approve client visit requests?', a: 'Go to Leave & Time → Client Visit → Team Requests tab. Review client visit requests from your team and approve or reject with comments.' },
      ],
    },
  ],

  project_manager: [
    {
      category: '📁 Project Management',
      items: [
        { q: 'How do I create a new project?', a: 'Go to Projects → New Project. Enter project name, description, client, start/end dates, budget, and add team members. Save to create the project.' },
        { q: 'How do I add a project logo?', a: 'Open the project → Settings tab → Upload Logo (PNG only). The logo appears on the project card and header.' },
        { q: 'How do I create issues / tasks?', a: 'Open the project → Issues tab → New Issue. Fill in title, description, assignees, priority, label, and due date. Issues appear on the board and in the assignee\'s My Tasks.' },
        { q: 'How do I track project financials?', a: 'Open the project → Financials tab. View budget, expenses logged, and remaining budget. Visible only to PM, CFO, CTO, and CEO.' },
        { q: 'How do I manage bugs?', a: 'Open the project → Bugs tab. Log bugs with severity, steps to reproduce, and assign to a developer. Track status: Open → In Progress → Fixed → Verified.' },
      ],
    },
  ],

  sales_head: [
    {
      category: '📈 Sales Module',
      items: [
        { q: 'How do I add a new tender or bid?', a: 'Go to Sales → Tenders & Bids → New Tender. Enter tender name, client, value, deadline, and status. Attach documents and assign team members.' },
        { q: 'How do I manage vendors and OEMs?', a: 'Go to Sales → Vendors / OEMs → Add Vendor. Enter vendor name, contact, category, and products/services. Link vendors to tenders as needed.' },
        { q: 'What does the Sales Dashboard show?', a: 'Active tenders, win/loss ratio, total pipeline value, recent updates, and upcoming tender deadlines.' },
      ],
    },
  ],

  office_admin: [
    {
      category: '🏢 Visitor Management',
      items: [
        { q: 'How do I register a visitor?', a: 'Go to Office → Visitor Management → New Visitor. Enter visitor name, company, purpose, whom they are visiting, and expected time.' },
        { q: 'How do I check out a visitor?', a: 'Go to Visitor Management, find the active visitor, and click Check Out. The exit time is recorded automatically.' },
        { q: 'Can I view the visitor log?', a: 'Yes. The Visitor Management page shows today\'s visitors by default. Use the date filter to view historical logs.' },
      ],
    },
  ],

  super_admin: [
    {
      category: '⚙️ System Administration',
      items: [
        { q: 'How do I manage user accounts?', a: 'Go to Admin → User Management. Create, edit, reset passwords, activate/deactivate users, and change roles. Each user is linked to an employee record.' },
        { q: 'How do I reset a user\'s password?', a: 'Go to Admin → User Management, find the user, and click Reset Password. A temporary password is generated and the user must change it on next login.' },
        { q: 'Can I access all modules?', a: 'Yes. Super Admin has unrestricted access to all modules: HR, IT, Finance, Projects, Sales, Office, and system configuration.' },
      ],
    },
  ],
};

// ── Role → FAQ sections mapping ───────────────────────────────────────────────

const ROLE_FAQ_MAP = {
  employee:          [...FAQ_BY_ROLE.employee],
  reporting_manager: [...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  team_lead:         [...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  project_lead:      [...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  project_manager:   [...FAQ_BY_ROLE.project_manager, ...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  hr_admin:          [...FAQ_BY_ROLE.hr_admin, ...FAQ_BY_ROLE.employee],
  hr_head:           [...FAQ_BY_ROLE.hr_admin, ...FAQ_BY_ROLE.employee],
  hr_executive:      [...FAQ_BY_ROLE.hr_admin, ...FAQ_BY_ROLE.employee],
  it_admin:          [...FAQ_BY_ROLE.it_admin, ...FAQ_BY_ROLE.employee],
  it_support:        [...FAQ_BY_ROLE.it_support, ...FAQ_BY_ROLE.employee],
  finance_executive: [...FAQ_BY_ROLE.finance_executive, ...FAQ_BY_ROLE.employee],
  finance_team:      [...FAQ_BY_ROLE.finance_executive, ...FAQ_BY_ROLE.employee],
  finance_head:      [...FAQ_BY_ROLE.finance_executive, ...FAQ_BY_ROLE.employee],
  ceo:               [...FAQ_BY_ROLE.ceo, ...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  cfo:               [...FAQ_BY_ROLE.ceo, ...FAQ_BY_ROLE.finance_executive, ...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  cto:               [...FAQ_BY_ROLE.ceo, ...FAQ_BY_ROLE.reporting_manager, ...FAQ_BY_ROLE.employee],
  sales_head:        [...FAQ_BY_ROLE.sales_head, ...FAQ_BY_ROLE.employee],
  sales_executive:   [...FAQ_BY_ROLE.sales_head, ...FAQ_BY_ROLE.employee],
  office_admin:      [...FAQ_BY_ROLE.office_admin, ...FAQ_BY_ROLE.employee],
  super_admin:       [...FAQ_BY_ROLE.super_admin, ...FAQ_BY_ROLE.hr_admin, ...FAQ_BY_ROLE.it_admin, ...FAQ_BY_ROLE.finance_executive, ...FAQ_BY_ROLE.employee],
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function FAQPanel({ isOpen, onClose, userRole }) {
  const [search, setSearch]     = useState('');
  const [openIdx, setOpenIdx]   = useState(null);
  const [openCats, setOpenCats] = useState({ 0: true });
  const panelRef                = useRef(null);

  const sections = ROLE_FAQ_MAP[userRole] || FAQ_BY_ROLE.employee;

  const filtered = search.trim()
    ? sections
        .map(cat => ({ ...cat, items: cat.items.filter(item =>
          item.q.toLowerCase().includes(search.toLowerCase()) ||
          item.a.toLowerCase().includes(search.toLowerCase())
        )}))
        .filter(cat => cat.items.length > 0)
    : sections;

  useEffect(() => {
    if (search.trim()) {
      const all = {};
      filtered.forEach((_, i) => { all[i] = true; });
      setOpenCats(all);
    } else {
      setOpenCats({ 0: true });
    }
    setOpenIdx(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (isOpen) { setSearch(''); setOpenIdx(null); setOpenCats({ 0: true }); }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const h = e => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [isOpen, onClose]);

  const toggleCat  = i   => setOpenCats(p => ({ ...p, [i]: !p[i] }));
  const toggleItem = key => setOpenIdx(p => p === key ? null : key);

  const totalQ = filtered.reduce((s, c) => s + c.items.length, 0);

  const roleLabel = (userRole || 'employee')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

  return (
    <>
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 299,
        opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'all' : 'none',
        transition: 'opacity 0.25s',
      }} onClick={onClose} />

      <div ref={panelRef} style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '480px', maxWidth: '95vw',
        background: '#fff', zIndex: 300,
        boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
        display: 'flex', flexDirection: 'column',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
      }}>

        {/* Header */}
        <div style={{
          background: theme.dark, padding: '1.25rem 1.5rem', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `3px solid ${theme.primary}`,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
              <span style={{ fontSize: '1.2rem' }}>📖</span>
              <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff' }}>Help & FAQ</span>
            </div>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.72rem', color: '#9CA3AF' }}>
              User manual for{' '}
              <span style={{ color: theme.primary, fontWeight: 600 }}>{roleLabel}</span>
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', color: '#D1D5DB',
            width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
            fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>✕</button>
        </div>

        {/* Search */}
        <div style={{ padding: '1rem 1.5rem', borderBottom: `1px solid ${theme.border}`, flexShrink: 0 }}>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
              fontSize: '0.85rem', color: theme.textMuted, pointerEvents: 'none',
            }}>🔍</span>
            <input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '0.6rem 2.25rem 0.6rem 2.25rem',
                border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem',
                fontSize: '0.875rem', color: theme.text, outline: 'none',
                background: theme.pageBg,
              }}
              onFocus={e => (e.target.style.borderColor = theme.primary)}
              onBlur={e => (e.target.style.borderColor = theme.border)}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                position: 'absolute', right: '0.625rem', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: theme.textMuted, fontSize: '0.85rem', lineHeight: 1,
              }}>✕</button>
            )}
          </div>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>
            {totalQ} question{totalQ !== 1 ? 's' : ''} {search ? 'found' : 'available'}
          </p>
        </div>

        {/* FAQ list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 0' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: theme.textMuted }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🤷</div>
              <p style={{ fontWeight: 600, margin: '0 0 0.25rem', color: theme.text }}>No results found</p>
              <p style={{ fontSize: '0.82rem', margin: 0 }}>Try different keywords</p>
            </div>
          ) : filtered.map((cat, ci) => (
            <div key={ci}>
              {/* Category toggle */}
              <button onClick={() => toggleCat(ci)} style={{
                width: '100%', background: openCats[ci] ? '#F8FAFC' : '#fff',
                border: 'none', cursor: 'pointer',
                padding: '0.7rem 1.5rem',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTop: `1px solid ${theme.border}`,
                transition: 'background 0.15s',
              }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: theme.text }}>
                  {cat.category}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    fontSize: '0.65rem', fontWeight: 700,
                    background: theme.primaryLight, color: theme.primaryDark,
                    padding: '0.1rem 0.45rem', borderRadius: '0.25rem',
                  }}>{cat.items.length}</span>
                  <span style={{
                    fontSize: '0.72rem', color: theme.textMuted, display: 'inline-block',
                    transform: openCats[ci] ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}>▾</span>
                </div>
              </button>

              {/* Items */}
              {openCats[ci] && cat.items.map((item, ii) => {
                const key = `${ci}-${ii}`;
                const active = openIdx === key;
                return (
                  <div key={ii} style={{
                    margin: '0.375rem 1rem',
                    border: `1.5px solid ${active ? theme.primary : theme.border}`,
                    borderRadius: '0.5rem', overflow: 'hidden',
                    transition: 'border-color 0.15s',
                  }}>
                    <button onClick={() => toggleItem(key)} style={{
                      width: '100%', background: active ? '#FFF7ED' : '#fff',
                      border: 'none', cursor: 'pointer',
                      padding: '0.65rem 0.875rem',
                      textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                      transition: 'background 0.15s',
                    }}>
                      <span style={{
                        fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
                        background: active ? theme.primary : theme.pageBg,
                        color: active ? '#fff' : theme.textMuted,
                        padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                        marginTop: '0.1rem', transition: 'all 0.15s',
                      }}>Q</span>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: theme.text, flex: 1, lineHeight: 1.4 }}>
                        {item.q}
                      </span>
                      <span style={{
                        fontSize: '0.7rem', color: theme.textMuted, flexShrink: 0,
                        display: 'inline-block', marginTop: '0.15rem',
                        transform: active ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                      }}>▾</span>
                    </button>
                    {active && (
                      <div style={{
                        padding: '0.7rem 0.875rem 0.875rem',
                        background: '#FFFBF5',
                        borderTop: '1px solid #FED7AA',
                        display: 'flex', gap: '0.6rem',
                      }}>
                        <span style={{
                          fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
                          background: '#10B981', color: '#fff',
                          padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                          alignSelf: 'flex-start', marginTop: '0.1rem',
                        }}>A</span>
                        <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151', lineHeight: 1.65, flex: 1 }}>
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1.5rem', borderTop: `1px solid ${theme.border}`,
          background: theme.pageBg, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <p style={{ margin: 0, fontSize: '0.72rem', color: theme.textMuted }}>
            Need more help? Contact HR or IT support.
          </p>
          <span style={{
            fontSize: '0.65rem', color: theme.textMuted,
            background: theme.border, padding: '0.15rem 0.5rem', borderRadius: '0.25rem',
          }}>Bonton Suite</span>
        </div>
      </div>
    </>
  );
}
